import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import PaymentModal from '../components/patient/PaymentModal';
import { patientApi } from '../api';
import { useClinicEvent } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { formatAge, formatDate, formatMoney, timeAgo, titleCase } from '../utils/format';

export default function Dues() {
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ due: 0, patients: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [payingFor, setPayingFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await patientApi.dues();
      setRows(res.data);
      setTotals(res.totals);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Someone collecting at the other desk should shorten this list here too.
  useClinicEvent(['payment:received', 'queue:changed'], load);

  const term = search.trim().toLowerCase();
  const visible = term
    ? rows.filter(
        (r) =>
          r.patient.name.toLowerCase().includes(term) ||
          r.patient.mobile.includes(term) ||
          r.patient.patientId.toLowerCase().includes(term),
      )
    : rows;

  return (
    <>
      <PageHeader
        title="Pending Payments"
        subtitle="Everyone who still owes something, longest outstanding first"
        icon="bi-cash-stack"
      >
        <Button variant="outline-secondary" onClick={load} title="Refresh">
          <i className="bi bi-arrow-clockwise" />
        </Button>
      </PageHeader>

      <Row className="g-4 mb-4">
        <Col xs={12} md={4}>
          <StatCard
            label="Total Outstanding"
            value={formatMoney(totals.due || 0)}
            icon="bi-exclamation-circle-fill"
            color="#e0563f"
          />
        </Col>
        <Col xs={6} md={4}>
          <StatCard
            label="Patients Owing"
            value={totals.patients || 0}
            icon="bi-people-fill"
            color="#d9a213"
          />
        </Col>
        <Col xs={6} md={4}>
          <StatCard
            label="Collected So Far"
            value={formatMoney(totals.paid || 0)}
            icon="bi-check-circle-fill"
            color="#1f9d61"
            hint="from these same patients"
          />
        </Col>
      </Row>

      <Card className="search-panel mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find someone in this list by name, mobile or patient ID"
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch('')} title="Clear">
                <i className="bi bi-x-lg" />
              </Button>
            )}
          </InputGroup>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          {visible.length} patient{visible.length === 1 ? '' : 's'} with a balance
        </Card.Header>

        {loading ? (
          <Loader label="Adding up the dues..." />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={term ? 'bi-search' : 'bi-emoji-smile'}
            title={term ? `Nobody in this list matches "${search}"` : 'Nothing outstanding'}
            hint={term ? 'Try the mobile number instead.' : 'Every patient has paid in full.'}
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Mobile</th>
                  <th className="text-end">Charged</th>
                  <th className="text-end">Paid</th>
                  <th className="text-end">Due</th>
                  <th className="d-none d-lg-table-cell">Pending since</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.patient._id} className="queue-row">
                    <td onClick={() => navigate(`/patients/${r.patient._id}`)}>
                      <div className="list-name">{r.patient.name}</div>
                      <div className="list-sub">
                        {r.patient.patientId} &middot; {titleCase(r.patient.gender)}, {formatAge(r.patient)}
                      </div>
                    </td>

                    <td>
                      <a
                        href={`tel:${r.patient.mobile}`}
                        className="fw-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.patient.mobile}
                      </a>
                    </td>

                    <td className="text-end" onClick={() => navigate(`/patients/${r.patient._id}`)}>
                      {formatMoney(r.billed)}
                    </td>
                    <td className="text-end text-success" onClick={() => navigate(`/patients/${r.patient._id}`)}>
                      {formatMoney(r.paid)}
                    </td>
                    <td className="text-end" onClick={() => navigate(`/patients/${r.patient._id}`)}>
                      <span className="fw-bold text-danger">{formatMoney(r.due)}</span>
                      {r.unpaidVisits > 1 && (
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          over {r.unpaidVisits} visits
                        </div>
                      )}
                    </td>

                    <td
                      className="d-none d-lg-table-cell small"
                      onClick={() => navigate(`/patients/${r.patient._id}`)}
                    >
                      {formatDate(r.oldestUnpaid)}
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        {timeAgo(r.oldestUnpaid)}
                      </div>
                    </td>

                    <td className="text-end">
                      <Button size="sm" className="btn-clinic" onClick={() => setPayingFor(r.patient)}>
                        <i className="bi bi-cash-coin me-1" /> Collect
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <PaymentModal
        show={!!payingFor}
        patientId={payingFor?._id}
        onHide={() => setPayingFor(null)}
        onChanged={load}
      />
    </>
  );
}
